import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useReducer, useEffect } from 'react';
import { differenceInYears } from 'date-fns';
import { makeStyles } from 'tss-react/mui';
import { Grid } from '@mui/material';
import { captureException } from '@sentry/react';
import { getStates, getCountries } from 'country-provider';
import NativeSelect from '@components/Fields/NativeSelect';
import errorCodeFromError from '@helpers/errorCodeFromError';
import HelpTextContent from '@components/UI/HelpTextContent';
import GenericModal from '@components/Modals/GenericModal';
import CoreButton from '@components/Buttons/CoreButton';
import { useLocalAppContext } from '@context/LocalAppContext';
import useSignOut from '@components/Hooks/useSignOut';
import useRegistrationTimeout from './hooks/useRegistrationTimeout';
import useTrackUserFlow from '@hooks/useTrackUserFlow';
import useTrackEvent from '@hooks/useTrackEvent';
import getCountriesSortedByNameExcludingRestricted from '@helpers/getCountriesSortedByNameExcludingRestricted';
import getValidCompleteSignUpInitialData from './helpers/getValidCompleteSignUpInitialData';
import ErrorWithCause from '@helpers/ErrorWithCause';
import TextInput from '@components/Fields/TextInput';
import CheckboxField from '@components/Fields/CheckboxField';
import ModalContent from '@components/Modals/components/ModalContent';
import ModalStatusBanner from '@components/Modals/components/ModalStatusBanner';
import { days, months, years } from './helpers/dateHelpers';
import useRegisterRequest from './helpers/useRegisterRequest';
import getValidationErrors from './helpers/getValidationErrors';
import validateRegister, { REGEX_PASSWORD_COMPLEXITY } from './helpers/validateRegister';
import BoxButton from '@components/Buttons/BoxButton';
const useStyles = makeStyles()((theme) => ({
    removePadding: {
        padding: '0',
    },
    container: {
        fontFamily: theme.typography.fontFamily,
        background: '#fff',
    },
    dateOfBirthHeading: {
        border: 'none',
        color: 'currentColor',
        fontSize: '12px',
        fontWeight: 700,
        marginBottom: '0px',
    },
    dateOfBirthHeadingError: {
        display: 'block',
        opacity: 1,
        fontSize: '11px',
        textAlign: 'right',
        paddingBottom: theme.spacing(1),
        transition: 'opacity 0.3s',
        color: theme.palette.error.main,
        fontWeight: 600,
    },
    dateOfBirthSelect: {
        margin: theme.spacing(2, 0),
    },
    checkboxLabel: {
        fontSize: '11px',
        fontWeight: 600,
        color: theme.palette.grey[400],
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    createAccountButton: {
        width: '100%',
        height: '50px',
    },
    existingAccountFooter: {
        fontSize: '11px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        fontWeight: 600,
        color: theme.palette.grey[400],
        paddingTop: theme.spacing(3),
    },
    siteLink: {
        fontSize: '11px',
        fontWeight: 600,
        color: theme.palette.primary.main,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    registrationError: {
        color: theme.palette.error.main,
        fontSize: '11px',
        fontWeight: 600,
        textAlign: 'center',
    },
    locationSelect: {
        '& label': {
            fontWeight: 600,
        },
    },
    dobLabel: {
        marginBottom: theme.spacing(-1),
    },
    subText: {
        fontWeight: 500,
        color: theme.palette.grey[500],
        fontSize: '11px',
    },
}));
const FIREBASE_ERR_PASSWORD_REQUIREMENTS = 'PASSWORD_DOES_NOT_MEET_REQUIREMENTS'; // NOSONAR
const RegistrationForm = ({ onRegister, completeSignUpInitialData }) => {
    var _a, _b, _c, _d, _e, _f;
    const { classes, cx } = useStyles();
    const { openModal } = useLocalAppContext();
    const { signOut } = useSignOut();
    const trackEvent = useTrackEvent();
    const trackUserFlow = useTrackUserFlow();
    const countries = getCountriesSortedByNameExcludingRestricted();
    const unitedStatesStates = getStates('US', true);
    const shouldShowMarketingOptIn = (selectedCountry) => { var _a; return !!((_a = countries.find((country) => country.iso2 === selectedCountry)) === null || _a === void 0 ? void 0 : _a.gdpr); };
    const isCompleteSignUp = !!completeSignUpInitialData;
    useEffect(() => {
        if (isCompleteSignUp) {
            trackUserFlow('account_register_step', { formStep: 'Complete signup form' });
        }
        else {
            trackUserFlow('account_register_step', { formStep: 'Email signup form' });
        }
    }, []);
    const validCompleteSignUpInitialData = getValidCompleteSignUpInitialData(completeSignUpInitialData);
    const initialState = {
        form: {
            email: (_a = validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.email) !== null && _a !== void 0 ? _a : '',
            handle: '',
            password: '',
            verifyPassword: '',
            DOBDay: (_b = validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.DOBDay) !== null && _b !== void 0 ? _b : '',
            DOBMonth: (_c = validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.DOBMonth) !== null && _c !== void 0 ? _c : '',
            DOBYear: (_d = validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.DOBYear) !== null && _d !== void 0 ? _d : '',
            country: (_e = validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.country) !== null && _e !== void 0 ? _e : '',
            state: (_f = validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.state) !== null && _f !== void 0 ? _f : '',
            terms: '',
            ageCheck: '',
            marketingOptIn: false,
        },
        loading: true,
        validation: {},
        disableSubmit: false,
        showErrors: false,
        showMarketingOptIn: (validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.country)
            ? shouldShowMarketingOptIn(validCompleteSignUpInitialData.country)
            : false,
        showRegistrationSuccessReauthModal: false,
    };
    const reducer = (state, action) => {
        switch (action.type) {
            case 'setError':
                return { ...state, loading: false };
            case 'setFormField':
                return {
                    ...state,
                    form: {
                        ...state.form,
                        [action.payload.fieldName]: action.payload.fieldValue,
                    },
                };
            case 'setFormValidation':
                return {
                    ...state,
                    validation: {
                        ...state.validation,
                        [action.payload.fieldName]: action.payload.validationError,
                    },
                };
            case 'showGDPROptIn':
                return {
                    ...state,
                    form: {
                        ...state.form,
                        marketingOptIn: false,
                    },
                    showMarketingOptIn: true,
                };
            case 'hideGDPROptIn':
                return {
                    ...state,
                    form: {
                        ...state.form,
                        marketingOptIn: true,
                    },
                    showMarketingOptIn: false,
                };
            case 'setMarketingOptIn':
                return {
                    ...state,
                    form: {
                        ...state.form,
                        marketingOptIn: action.payload.marketingOptIn,
                    },
                };
            case 'handleFormSubmit':
                return {
                    ...state,
                    disableSubmit: true,
                };
            case 'handleFormError':
                return {
                    ...state,
                    disableSubmit: false,
                    showErrors: true,
                    validation: {
                        ...state.validation,
                        ...action.payload.validationErrors,
                    },
                };
            case 'setValidDOB':
                return {
                    ...state,
                    validation: {
                        ...state.validation,
                        DOBDay: '',
                        DOBMonth: '',
                        DOBYear: '',
                    },
                };
            case 'showRegistrationSuccessReauthModal':
                return {
                    ...state,
                    showRegistrationSuccessReauthModal: true,
                };
            case 'closeRegistrationSuccessReauthModal':
                return {
                    ...state,
                    showRegistrationSuccessReauthModal: false,
                };
            default:
                throw new Error();
        }
    };
    const [state, dispatch] = useReducer(reducer, initialState);
    const { isRegistrationTimeout, checkRegistrationTimeout } = useRegistrationTimeout(dispatch);
    const onRegisterSuccess = async (response, request, reauthenticateUser) => {
        if (reauthenticateUser) {
            if (response.customToken) {
                onRegister(request.handle, response.customToken);
            }
            else {
                dispatch({ type: 'showRegistrationSuccessReauthModal' });
            }
        }
        else {
            onRegister(request.handle, undefined);
        }
    };
    const onRegisterError = (error) => {
        var _a, _b, _c, _d, _e, _f;
        const errorWithCause = new ErrorWithCause(error);
        const isCaughtMessage = errorWithCause.message === 'Fields are already in use';
        // Only send unexpected errors to Sentry
        if (((_a = errorWithCause === null || errorWithCause === void 0 ? void 0 : errorWithCause.response) === null || _a === void 0 ? void 0 : _a.status) !== 400 && !isCaughtMessage) {
            captureException(error, {
                extra: {
                    file: 'RegistrationForm.tsx',
                    errorCode: errorCodeFromError(error),
                    data: (_b = errorWithCause.response) === null || _b === void 0 ? void 0 : _b.data,
                },
            });
        }
        const errorsArr = (_e = (_d = (_c = errorWithCause.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.errors) !== null && _e !== void 0 ? _e : [];
        // Create an object of errors from a symfony response
        const validationErrors = errorsArr.reduce((errorState, currentError) => {
            return {
                ...errorState,
                [currentError.errorField]: currentError.errorMessage,
            };
        }, {});
        const firebaseError = error;
        // Create an object of errors from a firebase response
        if (firebaseError.code === 'auth/email-already-in-use' || firebaseError.message === 'EMAIL_EXISTS') {
            validationErrors.email = 'This email is already in use, you may need to try forgotten password.';
        }
        if (firebaseError.code === 'auth/weak-password' ||
            ((_f = firebaseError.message) === null || _f === void 0 ? void 0 : _f.includes(FIREBASE_ERR_PASSWORD_REQUIREMENTS))) {
            validationErrors.password = REGEX_PASSWORD_COMPLEXITY;
            validationErrors.verifyPassword = REGEX_PASSWORD_COMPLEXITY;
        }
        if (Object.keys(validationErrors).length === 0) {
            validationErrors.form = 'Oops! Something went wrong, please try again!';
        }
        dispatch({ type: 'handleFormError', payload: { validationErrors } });
    };
    const [registerState, registerSubmit] = useRegisterRequest({
        onSuccess: onRegisterSuccess,
        onFail: onRegisterError,
    });
    // Validate DOB
    useEffect(() => {
        const { form } = state;
        const validationError = getValidationErrors(form, 'DOBDay');
        if (!validationError) {
            dispatch({ type: 'setValidDOB' });
        }
    }, [state.form.DOBDay, state.form.DOBMonth, state.form.DOBYear]);
    // Validate Country
    useEffect(() => {
        const { form } = state;
        const validationError = getValidationErrors(form, 'country');
        if (!validationError) {
            dispatch({ type: 'setFormValidation', payload: { fieldName: 'country', validationError: '' } });
        }
    }, [state.form.country]);
    // Validate State
    useEffect(() => {
        const { form } = state;
        const validationError = getValidationErrors(form, 'state');
        if (!validationError) {
            dispatch({ type: 'setFormValidation', payload: { fieldName: 'state', validationError: '' } });
        }
    }, [state.form.state]);
    const handleChange = (e) => {
        const fieldName = e.currentTarget.name;
        const fieldValue = e.currentTarget.value;
        dispatch({ type: 'setFormField', payload: { fieldName, fieldValue } });
    };
    const handleCheck = (e) => {
        const fieldName = e.currentTarget.name;
        const fieldValue = e.currentTarget.checked ? 'true' : '';
        dispatch({
            type: 'setFormField',
            payload: {
                fieldName,
                fieldValue,
            },
        });
        if (e.currentTarget.checked) {
            dispatch({ type: 'setFormValidation', payload: { fieldName, validationError: '' } });
        }
    };
    const handleNativeSelect = (selectedValue, nativeEvent) => {
        if (selectedValue && (nativeEvent === null || nativeEvent === void 0 ? void 0 : nativeEvent.currentTarget.name)) {
            dispatch({
                type: 'setFormField',
                payload: {
                    fieldName: nativeEvent.currentTarget.name,
                    fieldValue: selectedValue,
                },
            });
        }
    };
    const handleNativeDisplayMarketing = (selection) => {
        if (shouldShowMarketingOptIn(selection)) {
            dispatch({ type: 'showGDPROptIn' });
        }
        else {
            dispatch({ type: 'hideGDPROptIn' });
        }
    };
    const handleNativeSelectWithMarketing = (selectedValue, nativeEvent) => {
        handleNativeSelect(selectedValue, nativeEvent);
        handleNativeDisplayMarketing(selectedValue);
    };
    const handleMarketing = (e) => {
        const fieldValue = e.currentTarget.checked;
        // auto opt in for countries outside of GDPR jurisdiction
        dispatch({ type: 'setMarketingOptIn', payload: { marketingOptIn: fieldValue } });
    };
    const handleBlur = (e) => {
        const { form } = state;
        const fieldName = e.currentTarget.name;
        const validationError = getValidationErrors(form, fieldName);
        dispatch({ type: 'setFormValidation', payload: { fieldName, validationError } });
    };
    const signUpButtonText = !registerState.loading && !isCompleteSignUp ? 'Create Account' : 'Complete Sign Up';
    const handleSubmit = async (e) => {
        e.preventDefault();
        const { form, validation } = state;
        trackEvent({
            type: 'account_registration',
            data: {
                eventName: 'account registration',
                label: signUpButtonText,
                moduleName: 'Button',
                position: 4,
            },
        });
        // this happens in this scenario: selecting US as a country => selecting a state => selecting the other country.
        // In that case, remove the state.
        if (!form.state.toUpperCase().startsWith(form.country.toUpperCase())) {
            form.state = '';
        }
        const registrationValidation = validateRegister(form, isCompleteSignUp);
        dispatch({ type: 'handleFormSubmit' });
        if (registrationValidation.passes()) {
            void registerSubmit(form);
        }
        else {
            checkRegistrationTimeout(registrationValidation.errors, dispatch);
            const errorsArr = Object.entries(registrationValidation.errors.errors);
            const errors = errorsArr.reduce((validationErrors, error) => {
                var _a;
                const field = error[0];
                const errorMsg = (_a = error[1][0]) !== null && _a !== void 0 ? _a : '';
                return {
                    ...validationErrors,
                    [field]: errorMsg,
                };
            }, {});
            dispatch({
                type: 'handleFormError',
                payload: {
                    validationErrors: {
                        ...validation,
                        ...errors,
                    },
                },
            });
        }
    };
    const handleReauth = async () => {
        try {
            await signOut();
            openModal({
                type: 'login',
                onSuccess: () => {
                    dispatch({ type: 'closeRegistrationSuccessReauthModal' });
                    openModal({ type: 'welcome', username: state.form.handle });
                    onRegister(state.form.handle, undefined);
                },
            });
        }
        catch (error) {
            captureException(error, {
                extra: {
                    file: 'RegistrationForm.tsx',
                    function: 'handleReauth',
                },
            });
        }
    };
    const { validation, showErrors, form, showMarketingOptIn } = state;
    // Get the first DOB validation error, we only want to display the first relevant error.
    const dobValidation = [validation.DOBMonth, validation.DOBDay, validation.DOBYear];
    const dobHasError = dobValidation.find((validationMsg) => validationMsg !== '');
    // How many full years are between 31 December 2013 and 11 February 2015?
    const { DOBYear, DOBDay, DOBMonth } = form;
    let age = null;
    if (DOBYear && DOBMonth && DOBDay) {
        age = differenceInYears(new Date(), new Date(parseInt(DOBYear, 10), parseInt(DOBMonth, 10) - 1, parseInt(DOBDay, 10)));
    }
    const combinedValidation = {
        ...validation,
        ...registerState.validation,
    };
    const serverError = !!state.validation.form;
    const renderedStatesOptions = unitedStatesStates
        .filter((unitedStatesState) => !unitedStatesState.restricted)
        .map((unitedStatesState) => ({
        label: unitedStatesState.name,
        value: unitedStatesState.iso2,
    }));
    const showEmail = !(validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.email);
    const showPassword = !isCompleteSignUp;
    const showDateOfBirth = !(validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.DOBDay) ||
        !(validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.DOBMonth) ||
        !(validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.DOBYear);
    const showCountry = !(validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.country) ||
        ((validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.country) &&
            !getCountries().some((country) => { var _a; return country.iso2.toLowerCase() === ((_a = validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.country) === null || _a === void 0 ? void 0 : _a.toLowerCase()); }));
    const showState = form.country.toUpperCase() === 'US' && !(validCompleteSignUpInitialData === null || validCompleteSignUpInitialData === void 0 ? void 0 : validCompleteSignUpInitialData.state);
    return (_jsxs("div", { className: cx('repeat-register', classes.container), children: [serverError && (_jsx("p", { role: "alert", className: classes.registrationError, children: state.validation.form })), _jsxs("form", { onSubmit: handleSubmit, method: "POST", className: classes.removePadding, children: [_jsxs(Grid, { container: true, spacing: 1, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(TextInput, { label: "Username", id: "handle", placeholder: "Repeat Username...", onBlur: handleBlur, onChange: handleChange, value: form.handle, error: !!combinedValidation.handle, errorMessage: combinedValidation.handle, autoFocus: true }) }), showEmail && (_jsx(Grid, { item: true, xs: 12, children: _jsx(TextInput, { label: _jsxs(_Fragment, { children: ["Email Address ", _jsx("span", { className: classes.subText, children: "(You must verify your email address)" })] }), id: "email", placeholder: "Your Email Address...", onBlur: handleBlur, onChange: handleChange, value: form.email, error: !!combinedValidation.email, errorMessage: combinedValidation.email, type: "email" }) })), showPassword && (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, xs: 6, children: _jsx(TextInput, { label: "Password", id: "password", placeholder: "Your Password...", onBlur: handleBlur, onChange: handleChange, value: form.password, error: !!combinedValidation.password, errorMessage: combinedValidation.password, type: "password" }) }), _jsx(Grid, { item: true, xs: 6, children: _jsx(TextInput, { label: "Confirm Password", id: "verifyPassword", placeholder: "Confirm Password...", onBlur: handleBlur, onChange: handleChange, value: form.verifyPassword, error: !!combinedValidation.verifyPassword, errorMessage: combinedValidation.verifyPassword, type: "password" }) })] })), showDateOfBirth && (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, xs: 12, className: classes.dobLabel, children: _jsxs("p", { className: classes.dateOfBirthHeading, children: ["Date of Birth ", _jsx("span", { className: classes.subText, children: "(Falsifying age may result in a permanent ban)" })] }) }), _jsx(Grid, { item: true, xs: 5, children: _jsx(NativeSelect, { onSelect: handleNativeSelect, name: "DOBMonth", id: "dobmonth", placeholder: "Month", hideLabel: true, label: "Month", options: months, selectedValue: form.DOBMonth, error: !!dobHasError && showErrors, dataTestId: "month-select", disabled: isRegistrationTimeout }) }), _jsx(Grid, { item: true, xs: 3, children: _jsx(NativeSelect, { onSelect: handleNativeSelect, name: "DOBDay", id: "dobDay", placeholder: "Day", hideLabel: true, label: "Day", options: days, selectedValue: form.DOBDay, error: !!dobHasError && showErrors, dataTestId: "day-select", disabled: isRegistrationTimeout }) }), _jsx(Grid, { item: true, xs: 4, children: _jsx(NativeSelect, { onSelect: handleNativeSelect, name: "DOBYear", id: "dobYear", placeholder: "Year", hideLabel: true, label: "Year", options: years, selectedValue: form.DOBYear, error: !!dobHasError && showErrors, dataTestId: "year-select", disabled: isRegistrationTimeout }) }), dobHasError && showErrors && (_jsx(Grid, { item: true, xs: 12, className: cx('helperText error', classes.dateOfBirthHeadingError), children: dobHasError }))] })), showCountry && (_jsx(Grid, { item: true, xs: 12, children: _jsx(NativeSelect, { onSelect: handleNativeSelectWithMarketing, name: "country", label: "Select Country", placeholder: "Choose Your Country", id: "country-select", options: countries.map((countryObj) => ({ label: countryObj.name, value: countryObj.iso2 })), selectedValue: form.country, error: !!combinedValidation.country, errorMessage: combinedValidation.country, className: classes.locationSelect, dataTestId: "country-select" }) })), showState && (_jsx(Grid, { item: true, xs: 12, children: _jsx(NativeSelect, { onSelect: handleNativeSelectWithMarketing, name: "state", label: "State", placeholder: "Choose your State", id: "state-select", options: renderedStatesOptions, selectedValue: form.state, error: !!combinedValidation.state, errorMessage: combinedValidation.state, className: classes.locationSelect, dataTestId: "state-select" }) })), showState && (_jsx(Grid, { item: true, xs: 12, children: _jsx(HelpTextContent, { label: "Is your state missing?", helpContent: _jsxs(_Fragment, { children: ["Residents of Louisiana are currently not eligible to participate in", ' ', _jsx("span", { "data-ignore": true, children: "Repeat's" }), " competitions due to cash prize restrictions in those states. If you have any further questions regarding this, please", ' ', _jsx("span", { "data-ignore": true, children: _jsx("a", { href: "https://support.repeat.gg/hc/en-us/requests/new", rel: "noopener noreferrer", target: "_blank", children: "contact support" }) }), "."] }) }) })), _jsx(Grid, { item: true, xs: 12, "data-testid": "terms-checkbox", children: _jsx(CheckboxField, { className: classes.checkboxLabel, label: _jsxs("span", { children: ["By clicking Create Account, you are indicating that you have read and acknowledge the", ' ', _jsx("a", { className: classes.siteLink, href: "/about/terms-and-conditions/", target: "_blank", children: "Terms & Conditions" }), ' ', "and", ' ', _jsx("a", { className: classes.siteLink, href: "/about/privacy-policy/", target: "_blank", children: "Privacy policy" }), "."] }), name: "terms", onChange: handleCheck, value: !!form.terms, hasError: !!validation.terms, helperText: validation.terms }) }), age && age >= 13 && age < 18 ? (_jsx(Grid, { item: true, xs: 12, "data-testid": "ageCheck-checkbox", children: _jsx(CheckboxField, { className: classes.checkboxLabel, label: _jsxs("span", { children: ["I am ", form.country.toUpperCase() === 'US' ? 'a Minor' : 'under 18', ", but my parent or legal guardian has read the", ' ', _jsx("a", { href: "/about/terms-and-conditions", target: "_blank", children: "Terms & Conditions" }), ' ', "and has given me permission to create an account."] }), name: "ageCheck", onChange: handleCheck, value: !!form.ageCheck, hasError: !!combinedValidation.ageCheck, helperText: combinedValidation.ageCheck }) })) : null, age && age >= 18 ? (_jsx(Grid, { item: true, xs: 12, "data-testid": "ageCheck-checkbox", children: _jsx(CheckboxField, { className: classes.checkboxLabel, label: `I confirm that I'm over 18 years old`, name: "ageCheck", onChange: handleCheck, value: !!form.ageCheck, hasError: !!combinedValidation.ageCheck, helperText: combinedValidation.ageCheck }) })) : null, showMarketingOptIn && (_jsx(Grid, { item: true, xs: 12, "data-testid": "marketingOptIn-checkbox", children: _jsx(CheckboxField, { className: classes.checkboxLabel, label: _jsxs(_Fragment, { children: ["I agree to receive promotional emails from ", _jsx("span", { "data-ignore": true, children: "Repeat" }), "\u00A0that may contain vouchers for free coins and other site promotions"] }), name: "marketingOptIn", onChange: handleMarketing, value: Boolean(form.marketingOptIn) }) }))] }), registerState.recaptchaEnabled && _jsx("div", { className: "g-recaptcha", "data-sitekey": registerState.recaptchaKey }), _jsxs(BoxButton, { className: cx('input-group', 'submit-btn', classes.createAccountButton), type: "submit", loading: registerState.loading, disabled: registerState.loading || isRegistrationTimeout, children: [registerState.loading && 'Processing registration...', signUpButtonText] })] }), _jsx(GenericModal, { show: state.showRegistrationSuccessReauthModal, children: _jsxs(ModalContent, { align: "center", children: [_jsx(ModalStatusBanner, { status: "success" }), _jsx("h2", { children: "Registration Successful" }), _jsx("section", { children: _jsx("p", { children: "Please login again to complete the process." }) }), _jsx(CoreButton, { onClick: handleReauth, children: "Login" })] }) })] }));
};
export default RegistrationForm;
